import { fmtDisplayDate } from '@utils/displayDate'
import { dateField, dateRangeField } from '@components/reports/specializedFilterFields'
import { describeFilters, basisPart } from '@components/reports/reportFilterSummary'
import { EARLIEST_DEFAULT } from './exagoAgedArGrid'
import { rollsWithRun } from './exagoAgedArJob'

/**
 * Spec 209 §7, as measured and refined by spec 214 §3.1 / §3.4: an Advanced (Exago) report's
 * "Apply Parameters" prompts → the Report Center filter bar, and everything Report Center
 * says about what a run ran with.
 *
 * Only the two dates are live: CB's ReportCenterCsv sets `latestDate` / `earliestDate` from
 * `endDate` / `startDate` and nothing else (spec 209 §2.6). Every other prompt runs at the
 * report's saved value and is STATED ("Saved in this report"), never offered as a control
 * that would send nothing.
 *
 * Spec 214 §3.1: Exago's date PromptText is boilerplate on every live report ("Latest
 * possible date. If comparing two date ranges, this is Current Period End Date."), so a
 * report that has BOTH dates gets ONE date-range control labelled "Date range", with the
 * presets every specialized report has; a report with one date gets one control, labelled
 * with its PromptText only when that is short and one clause, else "As of" / "Start date".
 * Every statement (run notes, criteria line, print subtitle, schedule note) uses those same
 * labels — never a report-wide "as of" sentence, never Exago's boilerplate.
 *
 * "Date range" holds for a report that also uses CB's currentStartDate / prevEndDate (spec 214
 * wave 4, withdrawing review C1's two period controls): CB sets earliestDate = currentStartDate
 * = the chosen start (ExagoReportCenterHelper.cs:76-89, cb-api exago/dates.js), and an
 * earliestDate entity parameter bounds the data itself (Billable Hours by Month's bounds
 * Billing_Entries), so earliest–latest IS the data's scope. A run states CB's derived current
 * period start / previous period end beside it (derivedParts).
 *
 * Every string here reaches the screen as React text (filter labels, notes) or a document's
 * criteria line (plain text), never HTML, so nothing here is escaped.
 *
 * A plain module (no React) so the page file stays component-only.
 */

export { EARLIEST_DEFAULT }

/**
 * The title before a report has said its own name, and the ONLY name a non-reviewed
 * Exago report reaches analytics under (spec 209 §3: firm-authored names never leave).
 */
export const ADVANCED_REPORT_NAME = 'Advanced Report'

/** Spec 214 §3.4: the heading over the saved-filter chips, and the lead of every document's line for them. */
export const SAVED_IN_REPORT = 'Saved in this report'

/**
 * Spec 214 §3.4: stated ONCE right after the "Saved in this report" heading, as visible text that is
 * also the chip list's accessible description — never a per-chip title (hover-only, and a
 * fallback name that made every chip read as this sentence).
 */
export const SAVED_LOCKED_NOTE = 'Set in Advanced Reports. Report Center can’t change these yet.'

/**
 * Stated after SAVED_LOCKED_NOTE, and as one more sentence of the documents' saved-filter
 * line, when cb-api says how the report's filters combine is unread (`notes.filterLogicUnread`):
 * Exago's filters carry And/Or and grouping, and CC-54456's DesignInfo does not send them, so
 * the chips must not read as limits that all apply (spec 214 review, finding 0).
 */
export const FILTER_LOGIC_NOTE = 'Report Center can’t tell yet whether this report’s filters must all match or whether any one is enough.'

/**
 * Stated on every run and document when cb-api says Exago computes part of the report from the
 * day it runs (`notes.usesToday`; cb-api decides it, and spec 214 §3.2 lists what sets it). Two
 * forms, picked by the statement it joins (usesTodaySentence):
 * this one only beside a date that statement states, since "these dates" must name something.
 */
export const USES_TODAY_SENTENCE = 'Exago computes part of this report from the day it runs, not from these dates.'

/** The same, for a statement that states no date (a no-dates report's run notes and documents). */
export const USES_TODAY_NO_DATES_SENTENCE = 'Exago computes part of this report from the day it runs.'

/**
 * The usesToday sentence for one statement: USES_TODAY_SENTENCE when that statement states at
 * least one of the date control's dates, else USES_TODAY_NO_DATES_SENTENCE. CB's derived current
 * period start / previous period end do not count: in a report with no date control they come
 * from each run's own day, so "not from these dates" would be false about them.
 */
export const usesTodaySentence = (statesDate) => (statesDate === true ? USES_TODAY_SENTENCE : USES_TODAY_NO_DATES_SENTENCE)

/** What CB's own earliest date is called when the run used it (§7). */
export const EARLIEST_DEFAULT_NOTE = '(Centerbase’s default)'

/** Said in place of a basis Report Center could not read (§7). */
export const BASIS_UNREAD = 'Basis: the firm’s setting, which Report Center could not read'

/** Spec 214 §3.1: the one control for a report with both dates. */
export const DATE_RANGE_LABEL = 'Date range'
/** Spec 214 §3.1: a lone end date whose PromptText is not a usable label. */
export const AS_OF_LABEL = 'As of'
/** Spec 214 §3.1: a lone start date whose PromptText is not a usable label. */
export const START_DATE_LABEL = 'Start date'
/** Spec 214 §3.1: a PromptText longer than this is Exago's explanation, not a label. */
export const PROMPT_LABEL_MAX = 32

/**
 * The CB-set parameters CB derives from each run's dates: the current period's start (the
 * chosen start, or 1 January of the end date's year) and the day before it. They never choose
 * the date control; a report that uses them still moves with each run's end date.
 */
const PERIOD_PARAMETERS = ['currentStartDate', 'prevEndDate']

/** `/info`'s `cbParameters` name CB's current-period start or previous-period end. */
const usesPeriodParameters = (cbParameters) => Array.isArray(cbParameters) && cbParameters.some((p) => PERIOD_PARAMETERS.includes(p))

/**
 * Exago's PromptText as a field label, or null when it is not one: longer than 32
 * characters, or more than one clause (a sentence break, a comma, a semicolon, a colon,
 * a question). One trailing "." or ":" is punctuation, not a second clause, and is dropped.
 */
export const promptLabel = (text) => {
  if (typeof text !== 'string') return null
  const t = text.trim().replace(/[.:]$/, '').trim()
  if (!t || t.length > PROMPT_LABEL_MAX) return null
  if (/[.,;:!?]/.test(t)) return null
  return t
}

const present = (v) => v !== null && v !== undefined

/**
 * `{ kind, label }` — which date control the report gets and what it is called.
 * `asOf` / `earliest` are each a PromptText (possibly blank) when the report prompts for
 * or uses that date, null/undefined when it does not.
 */
const modeOf = (asOf, earliest) => {
  if (present(asOf) && present(earliest)) return { kind: 'range', label: DATE_RANGE_LABEL }
  if (present(asOf)) return { kind: 'asOf', label: promptLabel(asOf) || AS_OF_LABEL }
  if (present(earliest)) return { kind: 'earliest', label: promptLabel(earliest) || START_DATE_LABEL }
  return { kind: 'none', label: null }
}

const promptTextOf = (d) => (present(d) ? (typeof d.label === 'string' ? d.label : '') : null)

/** The date control for an `/info` description (its `dates`: `{ label }` or null each). */
export const dateMode = (info) => modeOf(promptTextOf(info?.dates?.asOf), promptTextOf(info?.dates?.earliest))

/** The same for a run's own answer (its `parameters.labels`: a string or null each). */
export const ranDateMode = (parameters) => modeOf(parameters?.labels?.asOf, parameters?.labels?.earliest)

/**
 * A `?jobId=` reopen has the saved run but may not have `/info`: the run's own answer
 * describes the same things (its date labels, saved values, notes, layout), so the page
 * can offer the same filters for the next run. `/info`, when it arrives, wins.
 */
export const infoFromRun = (data) => (data ? {
  reviewed: false,
  report: { id: data.report.id, name: data.report.name, description: '' },
  layoutSignature: data.layoutSignature,
  dates: {
    asOf: present(data.parameters.labels.asOf) ? { label: data.parameters.labels.asOf } : null,
    earliest: present(data.parameters.labels.earliest) ? { label: data.parameters.labels.earliest } : null,
  },
  fixed: data.parameters.fixed,
  // The CB-set parameters the run states (cb-api sends only those the report uses), so a
  // reopen's schedule note knows the report moves with each run's end date (genericScheduleDatesNote).
  cbParameters: [
    ...(present(data.parameters.derived?.basis) ? ['isCashBasis'] : []),
    ...PERIOD_PARAMETERS.filter((p) => present(data.parameters.derived?.[p])),
  ],
  notes: data.notes,
} : null)

/**
 * Presets whose end is open (cbDateSettings resolves both to `{ from, to: '' }`): an empty end
 * date cannot run, so the range offers neither, as it offers no "All dates" (finding 17).
 */
export const OPEN_ENDED_PRESETS = Object.freeze(['future', 'todayAndFuture'])

/**
 * The live date control(s) (spec 214 §3.1). Both dates → ONE range keyed `dates`: its start
 * is `earliest`, its end `asOf`, and it offers no "All dates" and no open-ended preset (an
 * empty end date cannot run). One date → one date field on that key. Neither → none.
 */
export const promptFieldsFor = (info) => {
  const mode = dateMode(info)
  if (mode.kind === 'range') {
    return [dateRangeField({ key: 'dates', label: mode.label, startKey: 'earliest', endKey: 'asOf', omitAll: true, omitPresets: [...OPEN_ENDED_PRESETS] })]
  }
  if (mode.kind === 'asOf') return [dateField({ key: 'asOf', label: mode.label, valueKey: 'asOf' })]
  if (mode.kind === 'earliest') return [dateField({ key: 'earliest', label: mode.label, valueKey: 'earliest' })]
  return []
}

/**
 * The filter defaults: the end date is today; the start date is BLANK, so CB applies its own
 * defaults (earliest 1/1/1900, and a current period from 1 January of the end date's year).
 * Sending 1/1/1900 instead would move that current period to 1900 (spec 209 §2.6).
 */
export const promptDefaults = (today) => ({ asOf: today, earliest: '' })

/** The dates a run sends: the end date always, the start date only when the user chose one (§5). */
export const runDates = ({ asOf, earliest } = {}) => ({ asOf: asOf || '', earliest: earliest || '' })

/** "the As of date", "the End Date": a label as the object of a sentence. */
const datePhrase = (label) => (/date$/i.test(label) ? `the ${label}` : `the ${label} date`)

/** The end date, as the schedule note and the run refusal name it. */
const endPhrase = (mode) => {
  if (mode?.kind === 'range') return 'the end of the date range'
  if (mode?.kind === 'asOf') return datePhrase(mode.label)
  return 'the end date'
}

/** The start date, likewise; null when the report has no start date control. */
const startPhrase = (mode) => {
  if (mode?.kind === 'range') return 'the start of the date range'
  if (mode?.kind === 'earliest') return datePhrase(mode.label)
  return null
}

const capitalized = (s) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`

/** Why a run cannot start, as a sentence, or null. Named with the page's own labels (`dateMode`). */
export const runProblem = (filters, mode = {}) => {
  const { asOf, earliest } = runDates(filters)
  if (!asOf) return `Choose ${endPhrase(mode)} to run this report.`
  if (earliest && earliest > asOf) {
    if (mode.kind === 'range') return 'The date range must start on or before its end.'
    // A lone start date runs against today: its end date is not on the page.
    if (mode.kind === 'earliest') return `${capitalized(datePhrase(mode.label))} must be on or before today.`
    return `The start date must be on or before ${endPhrase(mode)}.`
  }
  return null
}

/** "End Date: 10/30/2026"; the earliest date adds "(Centerbase’s default)" when it was CB's own. */
export const datePart = (label, iso, { defaulted = false, runDate = false } = {}) => {
  if (!label || !iso) return null
  const day = fmtDisplayDate(iso)
  if (!day) return null
  return `${label}: ${day}${defaulted ? ` ${EARLIEST_DEFAULT_NOTE}` : ''}${runDate ? ' (the day it ran)' : ''}`
}

/**
 * "Date range: 01/01/2025 – 11/03/2026" (spec 214 §3.1). A start left blank is stated as
 * the date CB used, with "(Centerbase’s default)", exactly as a lone start date is (D1);
 * null without both ends.
 */
export const rangePart = (start, end, { defaulted = false, runDate = false } = {}) => {
  const from = start ? fmtDisplayDate(start) : ''
  const to = end ? fmtDisplayDate(end) : ''
  if (!from || !to) return null
  return `${DATE_RANGE_LABEL}: ${from}${defaulted ? ` ${EARLIEST_DEFAULT_NOTE}` : ''} – ${to}${runDate ? ' (the day it ran)' : ''}`
}

const datePartsFor = (mode, { asOf, earliest, earliestDefaulted = false, runDate = false }) => {
  if (mode.kind === 'range') return [rangePart(earliest, asOf, { defaulted: earliestDefaulted, runDate })].filter(Boolean)
  if (mode.kind === 'asOf') return [datePart(mode.label, asOf, { runDate })].filter(Boolean)
  if (mode.kind === 'earliest') return [datePart(mode.label, earliest, { defaulted: earliestDefaulted })].filter(Boolean)
  return []
}

/** The dates, as the page states them before a run: from the filters, in the control's labels. */
export const liveDateParts = (filters, info) => {
  const { asOf, earliest } = runDates(filters)
  // Left blank, CB's own earliest date applies — said so, before the run as after it.
  return datePartsFor(dateMode(info), { asOf, earliest: earliest || EARLIEST_DEFAULT, earliestDefaulted: !earliest })
}

/**
 * The dates a run actually ran with, from its own answer (`parameters`). `earliestDefaulted`
 * is true exactly when no start date was sent and CB applied its own 1/1/1900 — a user who
 * CHOSE 1/1/1900 sent it, and CB then starts the current period there too, so that is not
 * "Centerbase's default".
 */
export const ranDateParts = (parameters) => {
  if (!parameters) return []
  return datePartsFor(ranDateMode(parameters), {
    asOf: parameters.asOf,
    earliest: parameters.earliest,
    earliestDefaulted: parameters.earliestDefaulted === true,
    runDate: parameters.asOfMode === 'run-date',
  })
}

/** The basis sentence: "Cash basis", "Accrual basis", or that it could not be read (§7). */
export const basisSentence = (basis) => {
  if (basis === 'cash') return basisPart(true)
  if (basis === 'accrual') return basisPart(false)
  if (basis === 'unknown') return BASIS_UNREAD
  return null
}

/** The CB-set parameters the report uses, stated after a run (§7); only those cb-api returned. */
export const derivedParts = (derived) => {
  if (!derived) return []
  return [
    derived.currentStartDate ? `Current period start: ${fmtDisplayDate(derived.currentStartDate)}` : null,
    derived.prevEndDate ? `Previous period end: ${fmtDisplayDate(derived.prevEndDate)}` : null,
    basisSentence(derived.basis),
  ].filter(Boolean)
}

/**
 * One saved filter as its chip reads it (spec 214 §3.4): "Label: value", in cb-api's words.
 * A label Exago ends with ":" ("Entry Date:", live on 2026-11-03) loses it, so the chip never
 * reads "Entry Date:: …"; an empty value is "(blank)".
 */
export const savedFilterText = (p) => {
  const label = String(p?.label ?? '').trim().replace(/\s*:+$/, '')
  const value = p?.value === '' || p?.value == null ? '(blank)' : p.value
  return `${label}: ${value}`
}

/**
 * The saved filters as one line for a document: "Saved in this report: A: x; B: y"; null when
 * there are none. When how they combine is unread (`filterLogicUnread`), FILTER_LOGIC_NOTE
 * follows as one more sentence, so the line never reads as limits that all apply.
 */
export const savedFiltersStatement = (fixed, { filterLogicUnread = false } = {}) => {
  if (!Array.isArray(fixed) || fixed.length === 0) return null
  const line = `${SAVED_IN_REPORT}: ${fixed.map(savedFilterText).join('; ')}`
  if (filterLogicUnread !== true) return line
  return `${line}${/[.!?]$/.test(line) ? '' : '.'} ${FILTER_LOGIC_NOTE}`
}

/**
 * "Sorted by Client, then Balance Due (descending)" — stated only when Report Center can
 * name at least one sort (spec 214 §3.2). Sorts it cannot name are not counted out loud.
 */
export const sortSentence = (notes) => {
  const sorts = Array.isArray(notes?.sorts) ? notes.sorts : []
  if (!sorts.length) return null
  return `Sorted by ${sorts.map((s) => `${s.label}${s.ascending === false ? ' (descending)' : ''}`).join(', then ')}`
}

/** Exago's percent lines as one sentence of text for a document's criteria line, or null. */
export const percentLinesSentence = (rows) => {
  const whole = (rows || []).filter((r) => r.scope == null)
  if (!whole.length) return null
  return whole
    .map((r) => `Exago’s percentages — ${r.cells.map((c) => `${c.header}: ${c.text}`).join(', ')}`)
    .join(' · ')
}

/**
 * The criteria line every document states (CSV / XLSX / PDF / print / AI scope). Before a
 * run: the dates on the panel and /info's saved filters. After one: what the run says it
 * ran with — its dates, the CB-derived parameters, `usesToday`, the report's own percent
 * lines, and the saved filters last (their items joined with "; " so they cannot be read
 * as peers of the parts before them, and followed by FILTER_LOGIC_NOTE when `filterLogicUnread`).
 * `usesToday`'s sentence is the dated one only when this line states one of `dates`.
 * No TopN: every live report carries a TopN (top 10), and whether it is enabled cannot be
 * read (notes.topNUnread, spec 214 §2.8), so nothing is re-added and no limit is stated —
 * stating one is an open question for Andrew (spec 214 §4).
 */
export const describeGenericFilters = ({ dates = [], derived = [], usesToday = false, fixed = [], percents = null, filterLogicUnread = false } = {}) => describeFilters(
  ...dates,
  ...derived,
  usesToday ? usesTodaySentence(dates.some((d) => d != null && String(d).trim() !== '')) : null,
  percents,
  savedFiltersStatement(fixed, { filterLogicUnread }),
)

/** The print masthead's line under the firm name: the dates with their labels, or undefined. */
export const printSubtitleFor = (dateParts) => (dateParts.length ? dateParts.join(' · ') : undefined)

/**
 * What the schedule modal says about a recurring run's dates (spec 192's sentence, in the
 * page's own labels). A schedule saved with the end date on today asks for each run's own
 * day; any other date stays that date.
 */
const startNote = (filters, mode) => {
  const start = startPhrase(mode)
  if (!start) return ''
  return filters.earliest
    ? ` ${capitalized(start)} stays where you put it.`
    : ` ${capitalized(start)} is left blank, so each run uses Centerbase’s default.`
}

/**
 * Finding 20: a report with no date control has no end date on the page, so its note never
 * names one (nor "the date shown above"). Not undefined: RecurrenceEditor would then say
 * "repeats the date range shown above", just as false.
 */
export const NO_DATES_SCHEDULE_NOTE = 'This report takes no dates, so every run repeats it as it stands.'

/**
 * Spec 217 — a report the run day changes nothing about: no date control, no run-day formula
 * (`notes.usesToday`), no CB period parameters. Its schedule note is NO_DATES_SCHEDULE_NOTE,
 * and its schedule stores a fixed as-of date rather than `@today` (which it would ignore), so
 * the Schedules table says "Fixed date range" beside that note, never "Ends on the day of each
 * run" (cb-api can read only the stored request, not the report's dates).
 */
export const takesNoDates = (mode = {}, info = null) =>
  mode.kind === 'none' && info?.notes?.usesToday !== true && !usesPeriodParameters(info?.cbParameters)

/**
 * Review finding 11: a no-dates report that Exago computes partly from the run day
 * (`notes.usesToday`; cb-api decides it, and spec 214 §3.2 lists what sets it) does not repeat
 * as it stands, so its note says what moves instead.
 */
export const NO_DATES_USES_TODAY_SCHEDULE_NOTE = 'This report takes no dates, but Exago computes part of it from the day each run happens.'

/**
 * `info` is the page's description (`/info`, or a reopen's infoFromRun): with no date control,
 * its `notes.usesToday` and `cbParameters` decide the note (finding 11). A report that uses
 * CB's currentStartDate / prevEndDate moves with each run's end date (the schedule sends
 * `asOf=@today`), and there is no sentence for that yet, so the answer is null — say nothing
 * rather than something false. The caller decides how nothing is shown.
 */
export function genericScheduleDatesNote(filters = {}, today, mode = {}, info = null) {
  if (mode.kind === 'none') {
    if (info?.notes?.usesToday === true) return NO_DATES_USES_TODAY_SCHEDULE_NOTE
    if (usesPeriodParameters(info?.cbParameters)) return null
    return NO_DATES_SCHEDULE_NOTE
  }
  // A lone start date: its end date is not on the page, so only the start is stated.
  if (mode.kind === 'earliest') return startNote(filters, mode).trim()
  const end = endPhrase(mode)
  if (rollsWithRun(filters, today)) {
    return `Every run uses the day it runs as ${end}, in your time zone.${startNote(filters, mode)}`
  }
  return filters.asOf
    ? `Every run uses ${fmtDisplayDate(filters.asOf)} as ${end}, the date shown above. Set ${end} to today for a run that uses the day it runs.`
    : undefined
}
